import { useTranslations } from '@vocab/react';
import { Button, Inline } from 'braid-design-system';
import { useEffect } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';

import {
  REFERENCES_SELECTION_NAMESPACE,
  ReferencesList,
  type ModifiedReferenceCheck,
} from 'src/components/References/ReferencesList';
import {
  Page,
  PageSection,
  PageSectionState,
  ReferencesSelection,
  useTrackingClient,
} from 'src/context/tracking';
import { useEditorDrawer } from 'src/ui/EditorDrawer/EditorDrawer';

import translations from './.vocab';

export function ReferencesEditor({
  referenceChecks,
}: {
  referenceChecks: ModifiedReferenceCheck[];
}) {
  const client = useTrackingClient();
  const { t } = useTranslations(translations);
  const form = useForm();
  const { handleSubmit, getValues, setValue } = form;
  const { onEditorClose, onFormDirty } = useEditorDrawer();
  const { getValues: getValuesRoot, setValue: setValueRoot } = useFormContext();
  const { editingId } = useEditorDrawer();

  useEffect(() => {
    setValue(
      REFERENCES_SELECTION_NAMESPACE,
      getValuesRoot(REFERENCES_SELECTION_NAMESPACE),
    );

    client.trackView({
      view: Page.REVIEW_PAGE,
      eventName: 'profile_section_displayed',
      currentPage: Page.REVIEW_PAGE,
      currentPageSection: PageSection.REFERENCES,
      currentPageSectionState: PageSectionState.EDIT,
    });
  }, [setValue, getValuesRoot, client]);

  const handleSave = () => {
    setValueRoot(
      REFERENCES_SELECTION_NAMESPACE,
      getValues(REFERENCES_SELECTION_NAMESPACE),
    );

    client.trackLink({
      link: Page.REVIEW_PAGE,
      eventName: 'apply_references_update_succeeded',
      currentPage: Page.REVIEW_PAGE,
      currentPageSection: PageSection.REFERENCES,
      referencesSelection:
        getValuesRoot(REFERENCES_SELECTION_NAMESPACE) === 'include'
          ? ReferencesSelection.INCLUDE
          : ReferencesSelection.DONT_INCLUDE,
    });

    onEditorClose();
  };

  const handleCancel = () => {
    onEditorClose();
  };

  if (editingId) {
    return null;
  }

  return (
    <>
      <FormProvider {...form}>
        <ReferencesList
          referenceChecks={referenceChecks}
          onFormDirtyChange={onFormDirty}
        />
        <Inline space="small">
          <Button
            tone="formAccent"
            variant="solid"
            onClick={handleSubmit(handleSave)}
          >
            {t('Save')}
          </Button>
          <Button tone="formAccent" variant="soft" onClick={handleCancel}>
            {t('Cancel')}
          </Button>
        </Inline>
      </FormProvider>
    </>
  );
}
